import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import {
  PipelineDetail,
  PipelineListResponse,
  PipelineSaveRequest,
  PipelineStatusResponse,
  PipelineDataResponse,
  PublishDatasetResponse,
  PipelineHistoryResponse,
  PipelineRunDetailResponse,
} from '../types/pipeline';

const pipelineKeys = {
  all: ['pipelines'] as const,
  list: () => [...pipelineKeys.all, 'list'] as const,
  detail: (id: string) => [...pipelineKeys.all, 'detail', id] as const,
  status: (id: string) => [...pipelineKeys.all, 'status', id] as const,
  data: (id: string) => [...pipelineKeys.all, 'data', id] as const,
  history: (id: string) => [...pipelineKeys.all, 'history', id] as const,
  run: (id: string, runId: string) => [...pipelineKeys.all, 'run', id, runId] as const,
};

export function usePipelines() {
  return useQuery({
    queryKey: pipelineKeys.list(),
    queryFn: async () => {
      return apiClient.get<PipelineListResponse>('/pipelines');
    },
  });
}

export function usePipeline(id: string | null) {
  return useQuery({
    queryKey: pipelineKeys.detail(id || 'new'),
    queryFn: async () => {
      if (!id) {
        throw new Error('Missing pipeline id');
      }
      return apiClient.get<PipelineDetail>(`/pipelines/${id}`);
    },
    enabled: !!id,
  });
}

export function usePipelineStatus(id: string) {
  return useQuery({
    queryKey: pipelineKeys.status(id),
    queryFn: async () => {
      return apiClient.get<PipelineStatusResponse>(`/pipelines/${id}/status`);
    },
    enabled: !!id,
    refetchInterval: 5000, // Poll every 5 seconds for status updates
  });
}

export function usePipelineData(id: string) {
  return useQuery({
    queryKey: pipelineKeys.data(id),
    queryFn: async () => {
      return apiClient.get<PipelineDataResponse>(`/pipelines/${id}/data`);
    },
    enabled: !!id,
  });
}

export function usePipelineHistory(id: string) {
  return useQuery({
    queryKey: pipelineKeys.history(id),
    queryFn: async () => {
      return apiClient.get<PipelineHistoryResponse>(`/pipelines/${id}/history`);
    },
    enabled: !!id,
  });
}

export function usePipelineRun(pipelineId: string, runId: string) {
  return useQuery({
    queryKey: pipelineKeys.run(pipelineId, runId),
    queryFn: async () => {
      return apiClient.get<PipelineRunDetailResponse>(`/pipelines/${pipelineId}/runs/${runId}`);
    },
    enabled: !!(pipelineId && runId),
  });
}

export function useCreatePipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PipelineSaveRequest) => {
      return apiClient.post<PipelineDetail>('/pipelines', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.list() });
      toast.success('Pipeline created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create pipeline');
    },
  });
}

export function useUpdatePipeline(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PipelineSaveRequest) => {
      return apiClient.put<PipelineDetail>(`/pipelines/${id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.list() });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(id) });
      toast.success('Pipeline updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update pipeline');
    },
  });
}

export function useDeletePipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return apiClient.delete(`/pipelines/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.list() });
      toast.success('Pipeline deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete pipeline');
    },
  });
}

export function useExecutePipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return apiClient.post<{ runId: string; status: string }>(`/pipelines/${id}/run`);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.status(variables) });
      toast.success(`Pipeline execution started: ${data.runId}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to execute pipeline');
    },
  });
}

export function usePublishDataset(pipelineId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ datasetId, request }: { datasetId: string; request: { isPublic: boolean; expiresAt?: string } }) => {
      return apiClient.put<PublishDatasetResponse>(`/pipelines/${pipelineId}/datasets/${datasetId}`, request);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.data(pipelineId) });
      toast.success('Dataset published successfully');
      return data;
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to publish dataset');
    },
  });
}

export function useCopyToClipboard() {
  return useMutation({
    mutationFn: async (text: string) => {
      await navigator.clipboard.writeText(text);
      return true;
    },
    onSuccess: () => {
      toast.success('Copied to clipboard');
    },
    onError: () => {
      toast.error('Failed to copy to clipboard');
    },
  });
}

export function useDuplicatePipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pipelineId, name, copyHistory }: { pipelineId: string; name: string; copyHistory?: boolean }) => {
      return apiClient.post<PipelineDetail>(`/pipelines/${pipelineId}/duplicate`, { name, copyHistory });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.list() });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to duplicate pipeline');
    },
  });
}
